// client/src/pages/Register.tsx
import React from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/TranslationContext';
import { CheckCircle, Check, Video, Calendar, Sparkles } from 'lucide-react';

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    storeNumber: '',
    preferredDate: '',
    preferredTime: '',
    interestedFeatures: [] as string[],
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Simplified feature list - just what they're interested in seeing
  const features = [
    { key: 'fohTasks', label: 'FOH Tasks & Checklists', icon: '🏪' },
    { key: 'setups', label: 'Setup Sheets', icon: '📋' },
    { key: 'kitchen', label: 'Kitchen Operations', icon: '👨‍🍳' },
    { key: 'documentation', label: 'Employee Documentation', icon: '📄' },
    { key: 'training', label: 'Training & Development', icon: '🎓' },
    { key: 'evaluations', label: 'Performance Evaluations', icon: '⭐' },
    { key: 'leadership', label: 'Leadership Development', icon: '👑' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureToggle = (featureKey: string) => {
    setFormData(prev => ({
      ...prev,
      interestedFeatures: prev.interestedFeatures.includes(featureKey)
        ? prev.interestedFeatures.filter(f => f !== featureKey)
        : [...prev.interestedFeatures, featureKey]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate preferred date is at least 1 day in advance
    if (formData.preferredDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      // Ensure we parse the date part only for comparison
      const datePart = formData.preferredDate.split('T')[0];
      const selectedDate = new Date(`${datePart}T00:00:00`);

      if (isNaN(selectedDate.getTime()) || selectedDate < tomorrow) {
        toast({
          title: 'Invalid Date',
          description: 'Please select a date at least 1 day in advance for your demo.',
          variant: 'destructive'
        });
        setIsLoading(false);
        return;
      }
    }

    try {
      const leadData = {
        ...formData,
        interestedFeaturesLabels: formData.interestedFeatures.map(key =>
          features.find(f => f.key === key)?.label
        ).filter(Boolean),
        submissionDate: new Date().toISOString(),
        requestType: 'demo'
      };

      await api.post('/api/leads/capture', leadData);
      setIsSubmitted(true);
      toast({
        title: "Demo Request Received!",
        description: "We'll reach out shortly to schedule your personalized demo.",
      });
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      toast({
        title: "Submission Error",
        description: _castErr.response?.data?.message || "There was an error submitting your request. Please try again.",
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E51636]/8 via-white to-[#E51636]/3 flex items-center justify-center px-4 py-4 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#E51636]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#E51636]/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#E51636]/3 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-lg relative z-10">
          <div className="text-center mb-3 sm:mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#E51636] to-[#C41230] rounded-xl sm:rounded-2xl mb-2 sm:mb-4 shadow-xl">
              <span className="text-xl sm:text-2xl font-bold text-white">LD</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#E51636] to-[#C41230] bg-clip-text text-transparent">
              LD Growth
            </h1>
          </div>

          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden rounded-2xl sm:rounded-3xl">
            <div className="h-1 sm:h-1.5 bg-gradient-to-r from-[#E51636] via-[#C41230] to-[#E51636]"></div>
            <CardContent className="p-4 sm:p-8">
              <div className="text-center">
                {/* Success Icon */}
                <div className="mx-auto flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-[#E51636]/10 to-[#E51636]/5 mb-3 sm:mb-4 border-2 border-[#E51636]/20 shadow-lg">
                  <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-[#E51636]" />
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-[#E51636] mb-3">
                  We&apos;ll Be In Touch Soon!
                </h2>

                <div className="bg-gradient-to-r from-[#E51636]/5 via-[#E51636]/10 to-[#E51636]/5 rounded-xl p-3 sm:p-5 mb-4 sm:mb-6">
                  <p className="text-gray-700 text-sm sm:text-base leading-snug">
                    Get ready to see how LD Growth can transform your operations!
                  </p>
                  <p className="text-gray-700 text-sm sm:text-base leading-snug mt-2">
                    Expect an email from <span className="font-bold text-[#E51636]">Jonathon@ld-growth.com</span> within the next few hours to schedule your personalized demo.
                  </p>
                </div>

                {/* What's Next Section */}
                <div className="bg-gray-50 rounded-xl p-3 sm:p-5 mb-4 sm:mb-6 text-left">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2 sm:mb-3 text-center">What happens next?</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#E51636] text-white flex items-center justify-center text-xs sm:text-sm font-bold mt-0.5">1</div>
                      <p className="text-gray-700 text-xs sm:text-sm leading-snug">You&apos;ll receive a confirmation email with demo details</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#E51636] text-white flex items-center justify-center text-xs sm:text-sm font-bold mt-0.5">2</div>
                      <p className="text-gray-700 text-xs sm:text-sm leading-snug">We&apos;ll schedule a time that works best for you</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#E51636] text-white flex items-center justify-center text-xs sm:text-sm font-bold mt-0.5">3</div>
                      <p className="text-gray-700 text-xs sm:text-sm leading-snug">See LD Growth in action with a personalized walkthrough</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-[#E51636] to-[#C41230] hover:from-[#DD0031] hover:to-[#B01028] text-white px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] rounded-xl w-full sm:w-auto"
                >
                  Return to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Calculate tomorrow's date for the date picker minimum (YYYY-MM-DD format)
  const tomorrowObj = new Date();
  tomorrowObj.setDate(tomorrowObj.getDate() + 1);
  const minDate = tomorrowObj.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E51636]/8 via-white to-[#E51636]/3 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none hidden sm:block">
        <div className="absolute top-20 left-10 w-32 h-32 bg-[#E51636]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#E51636]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#E51636]/3 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        <div className="mx-auto w-full max-w-md sm:max-w-3xl">
          {/* Header Section */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#E51636] to-[#C41230] rounded-2xl sm:rounded-3xl mb-4 sm:mb-6 shadow-xl">
              <span className="text-2xl sm:text-3xl font-bold text-white">LD</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-[#E51636] to-[#C41230] bg-clip-text text-transparent mb-3 sm:mb-4">
              See LD Growth in Action
            </h1>
            <p className="text-base sm:text-xl text-gray-600 max-w-md sm:max-w-2xl mx-auto leading-relaxed mb-6">
              Book a personalized demo and see how LD Growth can transform your restaurant operations.
            </p>

            {/* Value Props */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6">
              <div className="flex items-center gap-2 text-sm sm:text-base text-gray-700">
                <Video className="h-5 w-5 text-[#E51636]" />
                <span className="font-medium">15-min personalized demo</span>
              </div>
              <div className="flex items-center gap-2 text-sm sm:text-base text-gray-700">
                <Calendar className="h-5 w-5 text-[#E51636]" />
                <span className="font-medium">Schedule at your convenience</span>
              </div>
              <div className="flex items-center gap-2 text-sm sm:text-base text-gray-700">
                <Sparkles className="h-5 w-5 text-[#E51636]" />
                <span className="font-medium">See it live</span>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden rounded-3xl">
            <div className="h-1 bg-gradient-to-r from-[#E51636] via-[#C41230] to-[#E51636]"></div>
            <CardContent className="p-5 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 bg-[#E51636] text-white rounded-full text-sm font-bold">1</span>
                    Your Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Full Name*
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Email Address*
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Phone Number*
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="storeNumber" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Store Number*
                      </label>
                      <input
                        id="storeNumber"
                        name="storeNumber"
                        type="text"
                        required
                        placeholder="e.g., 00727"
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.storeNumber}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Preferred Demo Time */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 bg-[#E51636] text-white rounded-full text-sm font-bold">2</span>
                    When works best for you?
                  </h3>
                  <p className="text-sm text-gray-600">Select your preferred day and time for a 15-minute demo.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="preferredDate" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Preferred Date*
                      </label>
                      <input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        required
                        min={minDate}
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.preferredDate}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label htmlFor="preferredTime" className="block text-sm font-semibold text-[#E51636] mb-2">
                        Preferred Time*
                      </label>
                      <input
                        id="preferredTime"
                        name="preferredTime"
                        type="time"
                        required
                        className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                        value={formData.preferredTime}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>



                {/* Features Interest */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 bg-[#E51636] text-white rounded-full text-sm font-bold">3</span>
                    What would you like to see? (Optional)
                  </h3>
                  <p className="text-sm text-gray-600">Select any features you&apos;re interested in exploring during the demo.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {features.map((feature) => {
                      const isSelected = formData.interestedFeatures.includes(feature.key);
                      return (
                        <div
                          key={feature.key}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-[#E51636] bg-[#E51636]/5 shadow-md'
                              : 'border-gray-200 hover:border-[#E51636]/50 hover:bg-gray-50'
                          }`}
                          onClick={() => handleFeatureToggle(feature.key)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{feature.icon}</span>
                              <span className="font-medium text-gray-900">{feature.label}</span>
                            </div>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected
                                  ? 'bg-[#E51636] border-[#E51636]'
                                  : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Additional Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#E51636] mb-2">
                    Anything specific you'd like to discuss? (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E51636]/20 focus:border-[#E51636] hover:border-[#E51636]/50 transition-all duration-200 bg-gray-50/50"
                    placeholder="Tell us about your specific needs or questions..."
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#E51636] to-[#C41230] hover:from-[#DD0031] hover:to-[#B01020] text-white h-14 text-lg font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.01] rounded-2xl"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Video className="h-5 w-5" />
                        <span>Book My Demo</span>
                      </div>
                    )}
                  </Button>
                  <div className="mt-3 text-center text-xs text-gray-500">
                    No commitment • Free personalized demo • Built by operators
                  </div>
                </div>

                {/* Footer */}
                <div className="text-sm text-center pt-4 border-t border-gray-200 mt-4">
                  <p className="text-gray-700 mb-2">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="font-medium text-[#E51636] hover:text-[#E51636]/90"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
